import { Controller } from '@hotwired/stimulus'
import { mapsReady } from '../utils/maps'

/**
 * Карта клубов тренера, а у тренера без клубов — его городов.
 *
 * Маркеры приходят из бэкенда через `data-coach-map-markers-value`: логотип
 * клуба (у города его нет) и ссылка на его страницу или каталог города.
 */
export default class CoachMapController extends Controller {
  static targets = ['canvas']
  static values = {
    markers: Array,
    zoom: { type: Number, default: 14 }
  }

  #map = null

  async connect() {
    const { maplibregl, createMap } = await mapsReady()
    if (this.#map || !this.element.isConnected) return

    const markers = this.markersValue.filter(
      (marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng)
    )
    if (markers.length === 0) return

    // Снимок страницы в кеше Turbo хранит разметку прошлой карты: без
    // очистки новая легла бы поверх неё вторым набором кнопок.
    this.canvasTarget.replaceChildren()

    try {
      this.#map = createMap(this.canvasTarget, {
        center: [markers[0].lng, markers[0].lat],
        zoom: this.zoomValue,
        attributionControl: false
      })
    } catch {
      // MapLibre бросает исключение, если в браузере нет WebGL2.
      this.element.hidden = true
      return
    }

    // Справа внизу свои кнопки масштаба — подпись OpenStreetMap уходит влево.
    this.#map.addControl(new maplibregl.AttributionControl(), 'bottom-left')

    const bounds = new maplibregl.LngLatBounds()
    markers.forEach((marker) => {
      new maplibregl.Marker({ element: this.#buildMarker(marker) })
        .setLngLat([marker.lng, marker.lat])
        .addTo(this.#map)
      bounds.extend([marker.lng, marker.lat])
    })

    if (markers.length > 1) {
      this.#map.fitBounds(bounds, { padding: 64, animate: false })
    }
  }

  disconnect() {
    this.#map?.remove()
    this.#map = null
  }

  zoomIn() {
    this.#map?.zoomIn()
  }

  zoomOut() {
    this.#map?.zoomOut()
  }

  #buildMarker({ href, name, logo }) {
    const marker = document.createElement(href ? 'a' : 'div')
    marker.className = 'coach-map-marker'
    if (href) {
      marker.href = href
      marker.setAttribute('aria-label', name || '')
    }

    if (logo) {
      const image = document.createElement('img')
      image.src = logo
      image.alt = ''
      image.loading = 'lazy'
      marker.append(image)
    }

    return marker
  }
}
